/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { FC, PropsWithChildren, useMemo, useState } from 'react';

import { ClientContainer, TeamContainer, UserContainer } from '@/providers';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Link } from 'react-router-dom';
import { useSignOut } from '@/hooks/api/auth';
import { APP_ROUTE } from '@/constants';
import TeamUpgradeDialog from '@/views/settings/team/components/team-upgrade-dialog';
import NewMemberModal from '@/views/settings/team/components/new-add-member-modal';
import { useSession } from '@/states/common';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { getSubscriptionName } from '@/views/settings/billing/utils';
import ContactSupportModal from '@/views/settings/team/components/contact-support-modal';
import { useTeamLimit } from '@/hooks/use-team-limit';
import { useTranslation } from 'react-i18next';
import LogoutDialog from './logout-modal';

const AppTopNavContainer = ({ children }: PropsWithChildren) => {
  return (
    <header
      className={cn(
        'h-[72px] px-6 py-4 border-b border-grayscale-100 shrink-0 flex items-center justify-between gap-3',
        'transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-[64px]'
      )}
    >
      {children}
    </header>
  );
};

const AppTopNav: FC<PropsWithChildren> = ({ children }) => {
  const { state: userState } = UserContainer.useContainer();
  const { state: clientState } = ClientContainer.useContainer();
  const { state: teamState } = TeamContainer.useContainer();
  const { data: session } = useSession();
  const { t } = useTranslation('common');
  const { permission, billing: billingInfo } = session;
  const signout = useSignOut();
  const [showInviteMembersModal, setShowInviteMembersModal] = React.useState(
    false
  );
  const [showUpgradeModal, setShowUpgradeModal] = React.useState(false);
  const [planName, setPlanName] = React.useState<'professional' | 'team'>(
    'team'
  );
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  const [showContactSupportModal, setShowContactSupportModal] = useState(false);
  const isLegacyUser = userState?.business?.billing?.isLegacyUser;
  const subscriptionPlanName = getSubscriptionName(
    userState?.business?.billing?.status,
    userState?.business?.billing?.subscriptionName
  );
  const teamAmount = teamState?.members?.length - 1;

  const {
    isLimitMembers,
    isAppSumoCustomer: isAppSumo,
    isTenCodePurchaser: isTenCode,
  } = useTeamLimit({
    teamAmount,
    userState,
  });

  const userRole = permission?.role ?? 'member';

  const getPlanName = useMemo(() => {
    if (isLegacyUser) {
      return subscriptionPlanName.plan;
    }
    return userState?.business?.billing?.paddleSubscription?.planName;
  }, [isLegacyUser, subscriptionPlanName, userState?.business?.billing]);

  const SLACK_INVITE_URL =
    'https://join.slack.com/t/viewmetrics-community/shared_invite/zt-360hwgpif-txc0z9vuxF~jWWjUXqJmUg';

  return (
    <AppTopNavContainer>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {/* Logo - only visible on smaller screens */}
        <div className="md:hidden flex items-center">
          <img src="/logo192.png" alt="ViewMetrics logo" className="h-8 w-8" />
        </div>
        {children}
      </div>
      <div className="flex items-center gap-4 h-full max-w-[40%]">
        <Tooltip>
          <TooltipProvider delayDuration={100}>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                intent="tertiary"
                size="icon"
                className="rounded-full cursor-pointer"
                asChild
              >
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <span className="flex items-center justify-center cursor-pointer border border-grayscale-200 rounded-full h-8 w-8 flex-shrink-0">
                      <i className="ti ti-help-circle text-base text-grayscale-600" />
                    </span>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem
                      className="focus:bg-primary-50 leading-none"
                      onClick={() => {
                        window.open(
                          'https://support.viewmetrics.com',
                          '_blank'
                        );
                      }}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <i className="ti ti-book text-lg text-grayscale-600" />
                        <span>{t('support-center', 'Support Center')}</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="focus:bg-primary-50 leading-none"
                      onClick={() => {
                        window.$crisp.push(['do', 'chat:show']);
                        window.$crisp.push(['do', 'chat:open']);
                      }}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <i className="ti ti-messages text-lg text-grayscale-600" />
                        <span>{t('chat-with-us', 'Chat with us')}</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="focus:bg-primary-50 leading-none"
                      onClick={() => {
                        window.open(SLACK_INVITE_URL, '_blank');
                      }}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <img
                          src="/icons/logo/slack.png"
                          alt="Slack"
                          className="w-5 h-5"
                        />
                        <span>{t('join-community', 'Join Community')}</span>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Need help?</p>
            </TooltipContent>
          </TooltipProvider>
        </Tooltip>

        <Separator orientation="vertical" className="h-1/2" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" intent="tertiary" size="icon" asChild>
              <span className="flex items-center gap-3 h-full pl-[2px] pr-2 w-fit min-w-0 flex-shrink">
                <Avatar>
                  <AvatarImage
                    src={userState.user?.avatar}
                    alt={userState.user?.name}
                  />
                  <AvatarFallback>
                    {userState.user?.name
                      ?.split(' ')
                      .slice(0, 2)
                      .map((word: string) => word.charAt(0))
                      .join('')}
                  </AvatarFallback>
                </Avatar>

                <span className="flex items-center gap-4 min-w-0 flex-shrink">
                  <span className="flex flex-col min-w-0 flex-shrink">
                    <h6 className="font-sans text-sm font-medium text-grayscale-900 max-w-full overflow-hidden whitespace-nowrap text-ellipsis">
                      {userState.user?.name}
                    </h6>
                    <p className="font-sans text-xs text-grayscale-600 max-w-full overflow-hidden whitespace-nowrap text-ellipsis">
                      {userState.user?.email}
                    </p>
                  </span>

                  <i className="ti ti-chevron-down text-grayscale-600" />
                </span>
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem
              className="focus:bg-primary-50 leading-none"
              asChild
            >
              <Link
                to={`/ws/${clientState?.selectedClient?.id}/${APP_ROUTE.SETTINGS}/${APP_ROUTE.PROFILE}`}
              >
                <div className="flex items-center gap-2 w-full">
                  <i className="ti ti-user-circle text-lg text-grayscale-600" />
                  <span>My Profile</span>
                </div>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="focus:bg-primary-50 leading-none"
              asChild
            >
              <Link
                to={`/ws/${clientState?.selectedClient?.id}/${APP_ROUTE.SETTINGS}/${APP_ROUTE.ACCOUNT}`}
              >
                <div className="flex items-center gap-2 w-full">
                  <i className="ti ti-switch text-lg text-grayscale-600" />
                  <span>Switch Business</span>
                </div>
              </Link>
            </DropdownMenuItem>
            {userRole !== 'member' && (
              <>
                <DropdownMenuItem
                  className="focus:bg-primary-50 leading-none"
                  asChild
                >
                  <Link
                    to={`/ws/${clientState?.selectedClient?.id}/${APP_ROUTE.SETTINGS}/${APP_ROUTE.BILLING}`}
                  >
                    <div className="flex items-center gap-2 w-full">
                      <i className="ti ti-arrow-big-up text-lg text-grayscale-600" />
                      <span className="flex items-center justify-between w-full gap-1">
                        Change Plan{' '}
                        <span className="text-xs font-medium text-grayscale-900 border border-grayscale-200 rounded-sm px-2.5 py-[3px]">
                          {getPlanName}
                        </span>
                      </span>
                    </div>
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem
                  className="focus:bg-primary-50 leading-none"
                  asChild
                >
                  <div
                    className="flex items-center gap-2 w-full"
                    onClick={() => {
                      if (isLimitMembers) {
                        if (isAppSumo || isTenCode) {
                          setShowContactSupportModal(true);
                        } else {
                          setPlanName('team');
                          setShowUpgradeModal(true);
                        }
                      } else {
                        setShowInviteMembersModal(true);
                      }
                    }}
                  >
                    <i className="text-lg ti ti-user-plus text-grayscale-600" />
                    <span>Invite New User</span>
                  </div>
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuItem
              className="focus:bg-primary-50 leading-none"
              asChild
            >
              <a
                href="https://support.viewmetrics.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="flex items-center gap-2 w-full">
                  <i className="ti ti-external-link text-lg text-grayscale-600" />
                  <span>Knowledge Base</span>
                </div>
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="focus:bg-primary-50 leading-none"
              onClick={() => {
                setShowLogoutConfirmation(true);
              }}
            >
              <DropdownMenuSeparator />
              <div className="flex items-center gap-2 w-full">
                <i className="ti ti-logout text-base text-red-500" />
                <span>Logout</span>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <NewMemberModal
          isOpen={showInviteMembersModal}
          onClose={() => setShowInviteMembersModal(false)}
        />
        <TeamUpgradeDialog
          open={showUpgradeModal}
          onOpenChange={setShowUpgradeModal}
          planName={planName}
        />
        <ContactSupportModal
          open={showContactSupportModal}
          onOpenChange={setShowContactSupportModal}
        />
        <LogoutDialog
          isOpen={showLogoutConfirmation}
          setIsOpen={setShowLogoutConfirmation}
          onLogut={() => signout()}
        />
      </div>
    </AppTopNavContainer>
  );
};

export { AppTopNavContainer, AppTopNav };
